import moment from 'moment';
import { Control, Controller, FieldErrors, UseFormTrigger } from 'react-hook-form';
import styled from 'styled-components';
import { useEffect } from 'react';

import { Modal, ModalBody, ModalFooter, ModalTitle } from 'design-system/src/components/Modal';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow from 'design-system/src/components/ButtonRow/ButtonRow';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage.tsx';
import { Typography } from 'design-system/src/components/Typography/Typography';

import { Dropdown, DropdownContainer, DropdownItem } from '../../Dropdown.tsx';
import { FormLabel } from '../../FormLabel';
import { DateInput, Input } from '../../Input';
import { TremfyaExpressEnrollmentFormDataDefaultValues } from '../../../api';
import { requiredErrorMessage, requiredErrorString, titleCase } from '../../../utils.tsx';
import { formatPhoneNumber } from 'shared-utils/utils/formatPhoneNumber';
import { sexValues, phoneTypeValues, stateValues } from './dropdownValues.ts';
import { ControllerWrapper } from '../../signup/ControllerWrapper.tsx';
import { TherapyTask } from '../../../api';
import { EMAIL_VALIDATION_REGEX, PHONE_VALIDATION_REGEX } from '../../../constants.ts';

const FormContent = styled.fieldset`
  all: unset;
  display: block;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px 0;

  container-type: inline-size;
  container-name: form-container;
`;

const Row = styled.div`
  display: grid;
  gap: 16px;

  @container form-container (width < 450px) {
    grid-template-columns: 1fr !important;
  }
`;

const TwoInputRow = styled(Row)`
  grid-template-columns: 1fr 1fr;
`;

const EmailRow = styled(Row)`
  grid-template-columns: 1fr;
`;

const CityRow = styled(Row)`
  grid-template-columns: 2fr 1fr 1fr;

  /* Force dropdown to respect grid layout */
  .MuiSelect-select,
  .MuiInputBase-root {
    width: 100% !important;
    min-width: 0 !important;
  }
`;

const StyledControllerWrapper = styled(ControllerWrapper)`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const getSexOptions = () => {
  return sexValues.map((sex) => (
    <DropdownItem key={sex} value={sex}>
      {sex}
    </DropdownItem>
  ));
};

const getPhoneTypeOptions = () => {
  return phoneTypeValues.map((phoneType) => (
    <DropdownItem key={phoneType} value={phoneType}>
      {titleCase(phoneType)}
    </DropdownItem>
  ));
};

const getStateOptions = () => {
  return stateValues.map((state) => (
    <DropdownItem key={state} value={state}>
      {state}
    </DropdownItem>
  ));
};

export type TremfyaEnrollmentPatientModalProps = {
  control: Control<TremfyaExpressEnrollmentFormDataDefaultValues>;
  errors: FieldErrors<TremfyaExpressEnrollmentFormDataDefaultValues>;
  therapyTaskValue: TherapyTask;
  isCompleted: boolean;
  isOpen: boolean;
  onClose: () => void;
  trigger: UseFormTrigger<TremfyaExpressEnrollmentFormDataDefaultValues>;
  patientFields: readonly (keyof TremfyaExpressEnrollmentFormDataDefaultValues)[];
};

export const TremfyaEnrollmentPatientModal = ({
  control,
  errors,
  therapyTaskValue,
  isCompleted,
  isOpen,
  onClose,
  trigger,
  patientFields,
}: TremfyaEnrollmentPatientModalProps) => {
  // Trigger validation for patient fields when modal opens
  useEffect(() => {
    if (isOpen) {
      trigger(patientFields);
    }
  }, [isOpen, trigger, patientFields]);

  if (!isOpen) return null;

  const isEmailRequired = therapyTaskValue === TherapyTask.NurseGuide;
  const emailLabel = isEmailRequired ? (
    'Email (must be real and unique to the patient)'
  ) : (
    <>
      Email {/* TODO: use accessible color */}
      <Typography as="span" color="var(--grayscale-300)" styledAs="bodySmallSpaceGrotesk">
        Optional
      </Typography>
    </>
  );

  return (
    <Modal onClose={onClose}>
      <ModalTitle>Patient</ModalTitle>
      <ModalBody>
        <FormContent disabled={isCompleted}>
          <TwoInputRow>
            <StyledControllerWrapper>
              <Controller
                control={control}
                name="patient_first_name"
                rules={{ required: true }}
                render={({ field }) => (
                  <Input
                    {...field}
                    label="First name"
                    autoComplete="off"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(event.target.value);
                    }}
                    hasError={!!errors.patient_first_name}
                  />
                )}
              />
              {errors.patient_first_name && requiredErrorMessage}
            </StyledControllerWrapper>
            <StyledControllerWrapper>
              <Controller
                control={control}
                name="patient_last_name"
                rules={{ required: true }}
                render={({ field }) => (
                  <Input
                    {...field}
                    label="Last name"
                    autoComplete="off"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(event.target.value);
                    }}
                    hasError={!!errors.patient_last_name}
                  />
                )}
              />
              {errors.patient_last_name && requiredErrorMessage}
            </StyledControllerWrapper>
          </TwoInputRow>

          <TwoInputRow>
            <StyledControllerWrapper>
              <Controller
                control={control}
                name="patient_dob"
                rules={{ required: true }}
                render={({ field }) => (
                  <DateInput
                    {...field}
                    id="patient_dob"
                    label="Date of birth"
                    value={field.value ? moment(field.value) : null}
                    onChange={(value) => {
                      field.onChange(value ? value.toISOString() : '');
                    }}
                    hasError={!!errors.patient_dob}
                    disabled={isCompleted}
                  />
                )}
              />
              {errors.patient_dob && requiredErrorMessage}
            </StyledControllerWrapper>
            <StyledControllerWrapper>
              <Controller
                name="patient_sex"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <DropdownContainer>
                    <FormLabel id="sex-label">Sex</FormLabel>
                    <Dropdown
                      {...field}
                      labelId="sex-label"
                      onChange={(e) => field.onChange(e.target.value)}
                      $hasError={!!errors.patient_sex}
                      disabled={isCompleted}
                    >
                      {getSexOptions()}
                    </Dropdown>
                    {errors.patient_sex && requiredErrorMessage}
                  </DropdownContainer>
                )}
              />
            </StyledControllerWrapper>
          </TwoInputRow>

          <TwoInputRow>
            <StyledControllerWrapper>
              <Controller
                control={control}
                name="patient_phone"
                rules={{
                  required: {
                    value: true,
                    message: requiredErrorString,
                  },
                  pattern: {
                    value: PHONE_VALIDATION_REGEX,
                    message: 'Invalid phone',
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    label="Phone"
                    autoComplete="off"
                    value={field.value ? formatPhoneNumber(field.value) : ''}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(event.target.value);
                    }}
                    hasError={!!errors.patient_phone}
                  />
                )}
              />
              <ErrorMessage visible={!!errors.patient_phone}>{errors.patient_phone?.message}</ErrorMessage>
            </StyledControllerWrapper>
            <StyledControllerWrapper>
              <Controller
                name="patient_phone_type"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <DropdownContainer>
                    <FormLabel id="phone-type-label">Type</FormLabel>
                    <Dropdown
                      {...field}
                      labelId="phone-type-label"
                      $hasError={!!errors.patient_phone_type}
                      disabled={isCompleted}
                    >
                      {getPhoneTypeOptions()}
                    </Dropdown>
                  </DropdownContainer>
                )}
              />
              {errors.patient_phone_type && requiredErrorMessage}
            </StyledControllerWrapper>
          </TwoInputRow>

          <StyledControllerWrapper>
            <Controller
              control={control}
              name="patient_address"
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Street address"
                  autoComplete="off"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    field.onChange(event.target.value);
                  }}
                  hasError={!!errors.patient_address}
                />
              )}
            />
            {errors.patient_address && requiredErrorMessage}
          </StyledControllerWrapper>

          <CityRow>
            <StyledControllerWrapper>
              <Controller
                control={control}
                name="patient_city"
                rules={{ required: true }}
                render={({ field }) => (
                  <Input
                    {...field}
                    label="City"
                    autoComplete="off"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(event.target.value);
                    }}
                    hasError={!!errors.patient_city}
                  />
                )}
              />
              {errors.patient_city && requiredErrorMessage}
            </StyledControllerWrapper>
            <StyledControllerWrapper>
              <Controller
                name="patient_state"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <DropdownContainer>
                    <FormLabel id="state-label">State</FormLabel>
                    <Dropdown
                      {...field}
                      labelId="state-label"
                      $hasError={!!errors.patient_state}
                      disabled={isCompleted}
                    >
                      {getStateOptions()}
                    </Dropdown>
                  </DropdownContainer>
                )}
              />
              {errors.patient_state && requiredErrorMessage}
            </StyledControllerWrapper>
            <StyledControllerWrapper>
              <Controller
                control={control}
                name="patient_zip"
                rules={{ required: true }}
                render={({ field }) => (
                  <Input
                    {...field}
                    label="Zip code"
                    autoComplete="off"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(event.target.value);
                    }}
                    hasError={!!errors.patient_zip}
                  />
                )}
              />
              {errors.patient_zip && requiredErrorMessage}
            </StyledControllerWrapper>
          </CityRow>

          <EmailRow>
            <StyledControllerWrapper>
              <Controller
                control={control}
                name="patient_email"
                rules={{
                  required: {
                    value: isEmailRequired,
                    message: requiredErrorString,
                  },
                  pattern: {
                    value: EMAIL_VALIDATION_REGEX,
                    message: 'Invalid email',
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    label={emailLabel}
                    autoComplete="off"
                    value={field.value || ''}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(event.target.value);
                    }}
                    hasError={!!errors.patient_email}
                  />
                )}
              />
              <ErrorMessage visible={!!errors.patient_email}>{errors.patient_email?.message}</ErrorMessage>
            </StyledControllerWrapper>
          </EmailRow>
        </FormContent>
      </ModalBody>
      <ModalFooter>
        <ButtonRow>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onClose}>Save</Button>
        </ButtonRow>
      </ModalFooter>
    </Modal>
  );
};

export default TremfyaEnrollmentPatientModal;
